import React, { useEffect, useState } from "react";
import { EditIcon, HelpIcon, SaveIcon } from "assets/icons";
import Input from "components/BaseWidgets/form/input";
import Select from "components/BaseWidgets/select";
import Button from "components/BaseWidgets/button";
import { getFamilyDetailsList } from "./helper";
import { useSelectOptions, useProfession } from "hooks/api/useListing";
import { mapToSelectOptions, displayTextOrDash } from "utils/formatHelper";
import { familySelectOptions } from "components/PersonProfile/helper";
import { useEditProfile } from "hooks/api/useProfile";
import { useQueryClient } from "@tanstack/react-query";
import Tooltip from "components/BaseWidgets/toolTip";

function FamilyPanel({ isEdit, id, familyDetails, isLock, blurredField = [] }) {
  const [editing, setEditing] = useState(false);
  const familyData = familyDetails?.family || {};
  const detailsList = getFamilyDetailsList(familyData);
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState(() =>
    Object.fromEntries(detailsList.map((item) => [item.label, item.value])),
  );

  const { familyType, familyStatus } = useSelectOptions();
  const { data: professionData } = useProfession({ search: "" });

  const selectProfession = mapToSelectOptions(professionData || []);
  const selectFamilyOptions = {
    "Family Type": familyType?.options,
    "Family Status": familyStatus?.options,
    "Father Occupation": selectProfession,
    "Mother Occupation": selectProfession,
    Sister: familySelectOptions?.["sibling"] || [],
    Brother: familySelectOptions?.["sibling"] || [],
    "Married Brother": familySelectOptions?.["sibling"] || [],
    "Married Sister": familySelectOptions?.["sibling"] || [],
  };

  useEffect(() => {
    setFormData(
      Object.fromEntries(detailsList.map((item) => [item.label, item.value])),
    );
  }, [familyDetails]);

  const handleChange = (label, value) => {
    if (value?.target) {
      setFormData((prev) => ({ ...prev, [label]: value.target.value }));
    } else {
      setFormData((prev) => ({ ...prev, [label]: value }));
    }
  };

  const editProfileMutation = useEditProfile();

  const normalizeValue = (field) => {
    if (field === null || field === undefined) return null;
    if (typeof field === "object") {
      if ("value" in field) return Number(field.value);
      if ("id" in field) return Number(field.id);
    }
    if (typeof field === "number") return field;

    if (!isNaN(field)) return Number(field);
    if (typeof field === "object" && Object.keys(field).length === 0)
      return null;
    if (Array.isArray(field) && field.length === 0) return null;
    return field?.id || field;
  };

  const prepareFamilyPayload = () => ({
    family_type: normalizeValue(formData["Family Type"]),
    father_occupation: normalizeValue(formData["Father Occupation"]),
    mother_occupation: normalizeValue(formData["Mother Occupation"]),
    num_sisters: normalizeValue(formData["Sister"]),
    num_brothers: normalizeValue(formData["Brother"]),
    num_sisters_married: normalizeValue(formData["Married Sister"]),
    num_brothers_married: normalizeValue(formData["Married Brother"]),
    father_name: formData["Father Name"] || null,
    mother_name: formData["Mother Name"] || null,
    family_status: normalizeValue(formData["Family Status"]),
  });

  const handleSave = () => {
    const payload = prepareFamilyPayload();

    editProfileMutation.mutate(payload, {
      onSuccess: () => {
        setEditing(false);
        queryClient.invalidateQueries({
          queryKey: ["my-profile"],
        });
      },
    });
  };

  // ✅ FIXED: Dynamic filtering for married siblings (robust numeric handling)
  const getOptions = (label) => {
    let options = selectFamilyOptions[label] || [];
    const siblingOptions = familySelectOptions?.["sibling"] || [];

    // Helper to safely get numeric value
    const getNumeric = (val) => {
      if (val === null || val === undefined) return 0;
      if (typeof val === "object") return Number(val.value ?? val.id ?? 0);
      return Number(val);
    };

    if (label === "Married Sister") {
      const numSisters = getNumeric(formData["Sister"]);
      // Dynamically build range [0..numSisters]
      options = siblingOptions.filter((opt) => Number(opt.value) <= numSisters);
    }

    if (label === "Married Brother") {
      const numBrothers = getNumeric(formData["Brother"]);
      // Dynamically build range [0..numBrothers]
      options = siblingOptions.filter(
        (opt) => Number(opt.value) <= numBrothers,
      );
    }

    return options;
  };

  return (
    <div id={id} className="family-panel-frame panel-frame">
      <div className="family-details">
        <div className="header flex items-center justify-between">
          <label className="family-label font-title-normal">Family</label>
          {isEdit && (
            <Button
              variant="custom"
              onClick={() => {
                if (editing) {
                  handleSave();
                } else {
                  setEditing(true);
                }
              }}
              className="edit-button flex items-center gap-1"
            >
              {editing ? (
                <>
                  <SaveIcon /> Save
                </>
              ) : (
                <>
                  <EditIcon /> Edit
                </>
              )}
            </Button>
          )}
        </div>
        <div
          className={`content-grid flex flex-col gap-4 ${
            isLock && "locked-card"
          }`}
        >
          {!editing
            ? detailsList.map((item) => (
                <div
                  key={item.label}
                  className="item-frame flex flex-col gap-1"
                >
                  <span className="item-label">{item.label}</span>
                  <div className="flex items-center gap-2">
                    <span
                      className={`item-value ${
                        blurredField.includes(item.label) &&
                        Number(
                        typeof formData[item.label] === "object"
                          ? formData[item.label]?.value ??
                            formData[item.label]?.id ??
                            formData[item.label]?.name
                          : formData[item.label]
                      ) !== 0
                          ? "blur-text"
                          : ""
                      }`}
                    >
                      {formData[item.label] === null ||
                        formData[item.label] === undefined ||
                        formData[item.label] === ""
                          ? "-"
                          : displayTextOrDash(
                              typeof formData[item.label] === "object"
                                ? formData[item.label]?.name ??
                                  formData[item.label]?.value ??
                                  formData[item.label]?.id
                                : formData[item.label]
                      )}
                    </span>
                    {blurredField.includes(item.label) &&
                      Number(
                        typeof formData[item.label] === "object"
                          ? formData[item.label]?.value ??
                            formData[item.label]?.id ??
                            formData[item.label]?.name
                          : formData[item.label]
                      ) !== 0 && (
                        <Tooltip title="You should update your details to see this">
                          <span>
                            <HelpIcon />
                          </span>
                        </Tooltip>
                      )}
                  </div>
                </div>
              ))
            : detailsList.map((item) => {
                const options = getOptions(item.label);
                const rawVal =
                        typeof formData[item.label] === "object"
                        ? formData[item.label]?.id ?? formData[item.label]?.value
                        : formData[item.label];
                return (
                  <div
                    key={item.label}
                    className="item-frame flex flex-col gap-1"
                  >
                    <span className="item-label">{item.label}</span>
                    {options?.length ? (
                      <Select
                        SkeletonHeight={24}
                        returnObject
                        label={""}
                        value={options.find(opt => opt.value === rawVal)}
                        options={options}
                        onChange={(val) => handleChange(item.label, val)}
                        inputClassName="item-input-select"
                      />
                    ) : (
                      <Input
                        id={item.label}
                        label={""}
                        value={formData[item.label]}
                        onChange={(val) => handleChange(item.label, val)}
                        inputClassname="item-input"
                      />
                    )}
                  </div>
                );
              })}
        </div>
      </div>
    </div>
  );
}

export default FamilyPanel;
